import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_WORKING_HOURS_EXCEPTIONS_RESOURCE } from 'src/features/workingHoursExceptions/api/useListWorkingHoursExceptions';
import { useUpdateWorkingHoursException } from 'src/features/workingHoursExceptions/api/useUpdateWorkingHoursException';
import WorkingHoursExceptionsFormModal from 'src/features/workingHoursExceptions/components/form/WorkingHoursExceptionsFormModal';
import { workingHoursMessages } from 'src/features/workingHoursExceptions/utils/messages';
import { WorkingHoursException } from 'src/features/workingHoursExceptions/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface WorkingHoursFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWorkingHoursException: WorkingHoursException;
    children?: React.ReactNode;
}

function UpdateWorkingHoursExceptionsFormModal<TData>({
    className,
    semantics,
    children,
    selectedWorkingHoursException,
}: WorkingHoursFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { siteId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateWorkingHoursException({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_WORKING_HOURS_EXCEPTIONS_RESOURCE] });
            toast({
                title: formatMessage(workingHoursMessages.updateWorkingHoursSuccess),
                description: formatMessage(workingHoursMessages.updateWorkingHoursSuccessDescription),
            });
        },
    });

    return (
        <WorkingHoursExceptionsFormModal
            selectedWorkingHoursException={selectedWorkingHoursException}
            className={className}
            mutate={(data, onSuccess) =>
                mutate({ data, hoursId: selectedWorkingHoursException.id, siteId, parkingId }, { onSuccess })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(workingHoursMessages.editWorkingHoursSubtitle)}
        >
            {children}
        </WorkingHoursExceptionsFormModal>
    );
}

export default UpdateWorkingHoursExceptionsFormModal;
